/**
 * Pricing calculation utilities for Merlin product pricing templates.
 *
 * These functions implement explicit margin-based pricing calculations
 * with clear formulas and validation.
 */

/**
 * Currency formatter for USD display.
 * Handles edge cases (null, undefined, NaN) gracefully.
 */
const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

/**
 * Format a numeric amount as USD currency string.
 *
 * @param {number} amount - The amount to format
 * @returns {string} Formatted currency string (e.g., "$14.29")
 */
export function formatCurrency(amount) {
  if (amount === null || amount === undefined || Number.isNaN(amount)) {
    return '$0.00';
  }
  return currencyFormatter.format(amount);
}

/**
 * Calculate final price from total cost and target margin percentage.
 *
 * Uses the explicit margin formula:
 *   Margin = (Price - Cost) / Price
 *   Therefore: Price = Cost / (1 - Margin)
 *
 * @param {number} totalCost - Total cost basis (COGS + fulfillment + fees)
 * @param {number} marginPercent - Target margin percentage (0-99)
 * @returns {object} Price breakdown with { price, marginAmount, marginPercent, markupPercent }
 * @throws {Error} If marginPercent >= 100 (would divide by zero)
 *
 * @example
 * // $10 cost, 30% margin = $14.29 price
 * const result = calculateFromMargin(10, 30);
 * // result.price === 14.29
 * // result.marginAmount === 4.29
 * // result.markupPercent === 42.9
 */
export function calculateFromMargin(totalCost, marginPercent) {
  // Validate inputs
  if (typeof totalCost !== 'number' || totalCost < 0) {
    return {
      price: 0,
      marginAmount: 0,
      marginPercent: marginPercent || 0,
      markupPercent: 0,
    };
  }

  if (marginPercent >= 100) {
    throw new Error('Margin cannot be 100% or higher (would divide by zero)');
  }

  if (marginPercent < 0) {
    throw new Error('Margin cannot be negative');
  }

  // Handle zero cost edge case
  if (totalCost === 0) {
    return {
      price: 0,
      marginAmount: 0,
      marginPercent,
      markupPercent: 0,
    };
  }

  // Margin formula: Price = Cost / (1 - Margin)
  const marginDecimal = marginPercent / 100;
  const price = totalCost / (1 - marginDecimal);
  const marginAmount = price - totalCost;

  // Calculate equivalent markup percentage for transparency
  // Markup = (Price - Cost) / Cost * 100
  const markupPercent = (marginAmount / totalCost) * 100;

  // Round to cents at the end
  return {
    price: Math.round(price * 100) / 100,
    marginAmount: Math.round(marginAmount * 100) / 100,
    marginPercent,
    markupPercent: Math.round(markupPercent * 10) / 10,
  };
}

/**
 * Calculate final price from total cost and target markup percentage.
 *
 * Uses the markup formula:
 *   Markup = (Price - Cost) / Cost
 *   Therefore: Price = Cost * (1 + Markup)
 *
 * @param {number} totalCost - Total cost basis (COGS + fulfillment + fees)
 * @param {number} markupPercent - Target markup percentage (0+)
 * @returns {object} Price breakdown with { price, marginAmount, markupPercent, marginPercent }
 *
 * @example
 * // $10 cost, 30% markup = $13 price
 * const result = calculateFromMarkup(10, 30);
 * // result.price === 13
 * // result.marginAmount === 3
 * // result.marginPercent === 23.08
 */
export function calculateFromMarkup(totalCost, markupPercent) {
  // Validate inputs
  if (typeof totalCost !== 'number' || totalCost < 0) {
    return {
      price: 0,
      marginAmount: 0,
      marginPercent: 0,
      markupPercent: markupPercent || 0,
    };
  }

  if (markupPercent < 0) {
    throw new Error('Markup cannot be negative');
  }

  // Handle zero cost edge case
  if (totalCost === 0) {
    return {
      price: 0,
      marginAmount: 0,
      marginPercent: 0,
      markupPercent,
    };
  }

  // Markup formula: Price = Cost * (1 + Markup)
  const markupDecimal = markupPercent / 100;
  const price = totalCost * (1 + markupDecimal);
  const marginAmount = price - totalCost;

  // Calculate equivalent margin percentage for transparency
  // Margin = (Price - Cost) / Price * 100
  const marginPercent = (marginAmount / price) * 100;

  // Round to cents at the end
  return {
    price: Math.round(price * 100) / 100,
    marginAmount: Math.round(marginAmount * 100) / 100,
    marginPercent: Math.round(marginPercent * 100) / 100,
    markupPercent,
  };
}

/**
 * Apply rounding to a price with a custom ending.
 *
 * Rounds price down to nearest dollar, then adds the rounding ending.
 *
 * @param {number} price - The calculated price to round
 * @param {number|string|null} roundingEnding - Decimal like 0.99, 0.95, 0.49, or null to skip rounding
 * @returns {number} Price with rounding applied
 *
 * @example
 * // $14.29 with 0.99 ending = $14.99
 * applyRounding(14.29, 0.99);
 *
 * @example
 * // $14.29 with 0.49 ending = $14.49
 * applyRounding(14.29, 0.49);
 */
export function applyRounding(price, roundingEnding) {
  // If no rounding ending, return price as-is
  if (roundingEnding === null || roundingEnding === undefined || roundingEnding === '') {
    return price;
  }

  // Convert to number if string
  const ending = typeof roundingEnding === 'string' ? parseFloat(roundingEnding) : roundingEnding;

  // Validate ending is a valid decimal (0-0.99)
  if (Number.isNaN(ending) || ending < 0 || ending >= 1) {
    return price;
  }

  // Handle edge case: if price < 1, return max of price and ending
  if (price < 1) {
    return Math.max(price, ending);
  }

  // Round down to nearest dollar, then add the ending
  const baseDollar = Math.floor(price);
  const roundedPrice = baseDollar + ending;

  // Round to cents
  return Math.round(roundedPrice * 100) / 100;
}

/**
 * Calculate total cost for a product including fulfillment costs.
 *
 * Priority for fulfillment cost:
 * 1. FC Optimizer data (fcBaselineMap) if available
 * 2. Template fallback (additionalFulfillmentCost)
 *
 * @param {object} product - Product with { cost, sku, weightOz, productFactor }
 * @param {object} template - Template with settings.additionalFulfillmentCost
 * @param {Map} fcBaselineMap - Optional Map of SKU -> { fulfillmentCost } from FC Optimizer
 * @returns {object} Cost breakdown with { cogs, fulfillmentCost, fulfillmentSource, additionalFees, totalCost }
 */
export function calculateTotalCost(product, template, fcBaselineMap = null) {
  // Apply product factor to get actual cost (e.g., $2.45 × 12 = $29.40 for bulk items)
  const baseCost = Number(product?.cost) || 0;
  const productFactor = Number(product?.productFactor) || 1;
  const cogs = baseCost * productFactor;
  const sku = product?.sku;
  const settings = template?.settings || {};

  let fulfillmentCost = 0;
  let fulfillmentSource = 'fallback';

  // Priority 1: FC Optimizer data
  if (fcBaselineMap && sku) {
    const fcData = fcBaselineMap.get(sku);
    if (fcData?.fulfillmentCost !== undefined && fcData.fulfillmentCost !== null) {
      fulfillmentCost = Number(fcData.fulfillmentCost);
      fulfillmentSource = 'fcOptimizer';
    }
  }

  // Priority 2: Template fallback
  if (fulfillmentSource === 'fallback') {
    fulfillmentCost = Number(settings.additionalFulfillmentCost) || 0;
  }

  // Additional fees from template (channel cost, inbound cost)
  const additionalFees = (Number(settings.additionalChannelCost) || 0) +
                         (Number(settings.additionalInboundCost) || 0);

  // Calculate total cost basis
  const totalCost = cogs + fulfillmentCost + additionalFees;

  return {
    cogs,
    fulfillmentCost,
    fulfillmentSource,
    additionalFees,
    totalCost: Math.round(totalCost * 100) / 100,
  };
}
